import React, { useCallback, useEffect, useState } from 'react';
import platformService from '../api/platformService';

const STATUS_OPTIONS = ['trialing', 'active', 'past_due', 'suspended', 'cancelled'];

const formatCurrency = (amount) => `Rp ${Number(amount || 0).toLocaleString('id-ID')}`;

// Digits-only amount <-> '1.250.000' display (IDR has no decimals here).
const formatThousands = (digits) => (digits ? Number(digits).toLocaleString('id-ID') : '');
const MAX_AMOUNT_DIGITS = 15; // below 1 quadrillion — the range the words helper covers

// Indonesian "terbilang" (amount in words) — JS port of the backend's
// App\Libraries\Terbilang::sayRupiah(), so the wording matches the
// app's own invoices.
const TERBILANG_WORDS = ['', 'Satu', 'Dua', 'Tiga', 'Empat', 'Lima', 'Enam', 'Tujuh', 'Delapan', 'Sembilan', 'Sepuluh', 'Sebelas'];
const terbilangPart = (n) => {
  if (n < 12) return ` ${TERBILANG_WORDS[n]}`;
  if (n < 20) return `${terbilangPart(n - 10)} Belas`;
  if (n < 100) return `${terbilangPart(Math.floor(n / 10))} Puluh${terbilangPart(n % 10)}`;
  if (n < 200) return ` Seratus${terbilangPart(n - 100)}`;
  if (n < 1000) return `${terbilangPart(Math.floor(n / 100))} Ratus${terbilangPart(n % 100)}`;
  if (n < 2000) return ` Seribu${terbilangPart(n - 1000)}`;
  if (n < 1e6) return `${terbilangPart(Math.floor(n / 1000))} Ribu${terbilangPart(n % 1000)}`;
  if (n < 1e9) return `${terbilangPart(Math.floor(n / 1e6))} Juta${terbilangPart(n % 1e6)}`;
  if (n < 1e12) return `${terbilangPart(Math.floor(n / 1e9))} Milyar${terbilangPart(n % 1e9)}`;
  if (n < 1e15) return `${terbilangPart(Math.floor(n / 1e12))} Trilyun${terbilangPart(n % 1e12)}`;
  return '';
};
const terbilangRupiah = (amount) => {
  const words = terbilangPart(Math.floor(Math.abs(Number(amount) || 0))).replace(/\s+/g, ' ').trim();
  return words ? `${words} Rupiah` : 'Nol Rupiah';
};

// Manual contract limits — mirror PlatformTenantController::assignContract().
const CYCLE_DAYS = { monthly: 30, yearly: 365 };
const MAX_PERIODS = { monthly: 36, yearly: 5 };
const MAX_CUSTOM_YEARS = 5;
const DAY_MS = 24 * 60 * 60 * 1000;

// Date-only helpers on 'YYYY-MM-DD' strings, done in UTC so the result
// never shifts a day with the browser's timezone.
const parseYmd = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(value || '') ? new Date(`${value}T00:00:00Z`) : null);
const toYmd = (date) => date.toISOString().slice(0, 10);
const addDays = (ymd, days) => {
  const date = parseYmd(ymd);
  return date ? toYmd(new Date(date.getTime() + days * DAY_MS)) : '';
};
const todayYmd = () => {
  const now = new Date();
  return toYmd(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};
// Backend dates are 'Y-m-d H:i:s' strings — show the date part only.
const formatDate = (value) => {
  const date = parseYmd(String(value || '').slice(0, 10));
  return date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—';
};
const isPast = (value) => {
  const date = value ? new Date(String(value).replace(' ', 'T')) : null;
  return date && !Number.isNaN(date.getTime()) && date <= new Date();
};

// One compact contact line in the Tenant column — '—' when not provided.
const ContactLine = ({ label, value, href, external = false }) => (
  <div className="flex gap-1 min-w-0">
    <span className="text-gray-400 flex-shrink-0">{label}:</span>
    {value ? (
      <a
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className="text-gray-600 hover:underline hover:text-primary-600 break-all"
      >
        {value}
      </a>
    ) : (
      <span>—</span>
    )}
  </div>
);

const BillingCell = ({ tenant }) => {
  if (tenant.billing_state === 'contract' && tenant.contract) {
    const { contract } = tenant;
    return (
      <div className="space-y-1">
        <span className={`badge ${tenant.contract_expired ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'}`}>
          {tenant.contract_expired ? 'Contract expired' : 'Contract'}
        </span>
        <div className="text-xs text-gray-500 whitespace-nowrap">
          Until {formatDate(contract.current_period_end)} &middot; {contract.source === 'manual' ? 'Manual' : 'Midtrans'}
        </div>
      </div>
    );
  }

  if (tenant.billing_state === 'trial') {
    const ended = isPast(tenant.trial_ends_at);
    return (
      <div className="space-y-1">
        <span className="badge bg-blue-50 text-blue-700">Trial</span>
        {tenant.trial_ends_at && (
          <div className={`text-xs whitespace-nowrap ${ended ? 'text-red-600' : 'text-gray-500'}`}>
            {ended ? 'Trial ended' : 'Ends'} {formatDate(tenant.trial_ends_at)}
          </div>
        )}
      </div>
    );
  }

  return <span className="badge bg-gray-100 text-gray-700">No contract</span>;
};

const ManualContractModal = ({ tenant, plans, onCancel, onSaved }) => {
  const selectablePlans = plans.filter((p) => p.is_active !== false);
  const initialPlan = selectablePlans.find((p) => p.code === tenant.plan_code) || selectablePlans[0];
  const today = todayYmd();

  const [form, setForm] = useState({
    plan_code: initialPlan?.code || '',
    period_mode: 'cycle',
    billing_cycle: 'monthly',
    period_count: 1,
    start_date: today,
    end_date: addDays(today, 30),
    payment_reference: '',
    notes: '',
  });
  const [amount, setAmount] = useState('');
  const [amountEdited, setAmountEdited] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const plan = plans.find((p) => p.code === form.plan_code);

  const periodCount = parseInt(form.period_count, 10);
  const customDays = form.period_mode === 'custom' && parseYmd(form.start_date) && parseYmd(form.end_date)
    ? Math.round((parseYmd(form.end_date) - parseYmd(form.start_date)) / DAY_MS)
    : 0;
  const endDate = form.period_mode === 'cycle'
    ? (periodCount > 0 ? addDays(form.start_date, periodCount * CYCLE_DAYS[form.billing_cycle]) : '')
    : form.end_date;

  // Suggested amount — the plan price for the chosen period. Kept in sync
  // until the owner types their own amount.
  const suggestedAmount = (() => {
    if (!plan) return '';
    if (form.period_mode === 'cycle') {
      if (!(periodCount > 0)) return '';
      const price = form.billing_cycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
      return Number(price || 0) * periodCount;
    }
    return customDays > 0 ? Number(plan.price_monthly || 0) * Math.ceil(customDays / 30) : '';
  })();

  useEffect(() => {
    if (!amountEdited) setAmount(suggestedAmount === '' ? '' : String(Math.round(suggestedAmount)));
  }, [suggestedAmount, amountEdited]);

  const hasActiveContract = tenant.billing_state === 'contract' && tenant.contract && !tenant.contract_expired;

  const validate = () => {
    if (!plan) return 'Please select a plan.';
    if (!parseYmd(form.start_date)) return 'Please enter a valid start date.';
    if (form.period_mode === 'cycle') {
      const max = MAX_PERIODS[form.billing_cycle];
      if (!Number.isInteger(periodCount) || periodCount < 1 || periodCount > max) {
        return `Number of periods must be between 1 and ${max}.`;
      }
    } else {
      if (!parseYmd(form.end_date)) return 'Please enter a valid end date.';
      if (customDays < 1) return 'End date must be after the start date.';
      const maxEnd = parseYmd(form.start_date);
      maxEnd.setUTCFullYear(maxEnd.getUTCFullYear() + MAX_CUSTOM_YEARS);
      if (parseYmd(form.end_date) > maxEnd) return `Custom range cannot exceed ${MAX_CUSTOM_YEARS} years.`;
    }
    if (amount === '' || Number.isNaN(Number(amount)) || Number(amount) < 0) {
      return 'Amount must be a number greater than or equal to 0.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    const payload = {
      plan_code: form.plan_code,
      period_mode: form.period_mode,
      start_date: form.start_date,
      amount: Number(amount),
      payment_reference: form.payment_reference.trim(),
      notes: form.notes.trim(),
      send_email: sendEmail,
    };
    if (form.period_mode === 'cycle') {
      payload.billing_cycle = form.billing_cycle;
      payload.period_count = periodCount;
    } else {
      payload.end_date = form.end_date;
    }

    try {
      const res = await platformService.assignTenantContract(tenant._id, payload);
      onSaved(res.message || 'Contract assigned.');
    } catch (err) {
      setError(err.response?.data?.messages?.error || 'Failed to assign contract.');
    } finally {
      setSaving(false);
    }
  };

  const modeButton = (mode, label) => (
    <button
      type="button"
      onClick={() => setField('period_mode', mode)}
      className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
        form.period_mode === mode ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold text-gray-900">Manual Contract</h2>
        <p className="text-sm text-gray-500 mb-4">{tenant.name} ({tenant.subdomain})</p>

        {hasActiveContract && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-yellow-700 text-sm">
              This replaces the current contract ending {formatDate(tenant.contract.current_period_end)}.
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select className="input-field" value={form.plan_code} onChange={(e) => setField('plan_code', e.target.value)} required>
              {selectablePlans.map((p) => (
                <option key={p.code} value={p.code}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">Contract Period</p>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {modeButton('cycle', 'By Billing Cycle')}
              {modeButton('custom', 'Custom Date Range')}
            </div>
          </div>

          {form.period_mode === 'cycle' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select className="input-field" value={form.billing_cycle} onChange={(e) => setField('billing_cycle', e.target.value)}>
                  <option value="monthly">Monthly (30 days)</option>
                  <option value="yearly">Yearly (365 days)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Periods</label>
                <input
                  type="number"
                  min="1"
                  max={MAX_PERIODS[form.billing_cycle]}
                  className="input-field"
                  value={form.period_count}
                  onChange={(e) => setField('period_count', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" className="input-field" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <div className="input-field bg-gray-50 text-gray-700">{endDate ? formatDate(endDate) : '—'}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" className="input-field" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  className="input-field"
                  min={addDays(form.start_date, 1)}
                  value={form.end_date}
                  onChange={(e) => setField('end_date', e.target.value)}
                  required
                />
              </div>
              <p className="sm:col-span-2 text-xs text-gray-500 -mt-2">
                {customDays > 0 ? `${customDays} days — valid through the end of the end date.` : 'End date must be after the start date.'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (IDR)</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none">Rp</span>
              <input
                type="text"
                inputMode="numeric"
                className="input-field !pl-10"
                value={formatThousands(amount)}
                onChange={(e) => {
                  setAmount(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, MAX_AMOUNT_DIGITS));
                  setAmountEdited(true);
                }}
                placeholder="0"
                required
              />
            </div>
            {amount !== '' && (
              <p className="text-xs text-gray-600 mt-1 italic">In words: {terbilangRupiah(amount)}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {suggestedAmount !== '' ? `Suggested from plan price: ${formatCurrency(suggestedAmount)}` : 'Enter the amount received.'}
              {amountEdited && suggestedAmount !== '' && (
                <button type="button" className="ml-2 text-primary-600 hover:underline" onClick={() => setAmountEdited(false)}>
                  Use suggested
                </button>
              )}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              className="input-field"
              maxLength={100}
              value={form.payment_reference}
              onChange={(e) => setField('payment_reference', e.target.value)}
              placeholder="e.g. bank transfer ref. / PO number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input-field"
              rows={3}
              maxLength={500}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <span>
              Send activation email to tenant admin
              <span className="block text-xs text-gray-500">
                Notifies {tenant.admin_email || 'the tenant admin (no admin email on file)'} that the account is active, with the plan, contract period and a login link.
              </span>
            </span>
          </label>

          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            Manual contracts activate the tenant immediately and record a paid invoice.
            Use this only after payment has been received outside Midtrans.
          </p>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || !plan}>{saving ? 'Saving...' : 'Assign Contract'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  trialing: 'bg-blue-50 text-blue-700',
  active: 'bg-green-50 text-green-700',
  past_due: 'bg-yellow-50 text-yellow-700',
  suspended: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

// Normalizes a stored phone number into wa.me's expected digits-only format
// (leading '0' -> Indonesia's '62' country code), matching the wa.me pattern
// already used on the tenant-facing landing page (admin/src/pages/LandingPage.js).
const toWhatsAppLink = (phone) => {
  const digits = phone.replace(/\D/g, '');
  const withCountryCode = digits.startsWith('0') ? `62${digits.slice(1)}` : digits;
  return `https://wa.me/${withCountryCode}`;
};

const TenantsPage = () => {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [contractTenant, setContractTenant] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const load = useCallback(() => {
    setIsLoading(true);
    Promise.all([platformService.getTenants(), platformService.getPlans()])
      .then(([tenantsRes, plansRes]) => {
        if (tenantsRes.status === 'success') setTenants(tenantsRes.data);
        if (plansRes.status === 'success') setPlans(plansRes.data);
      })
      .catch(() => setError('Failed to load tenants.'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleContractSaved = (message) => {
    setContractTenant(null);
    setActionError('');
    setSuccessMessage(message);
    load();
  };

  const handleStatusChange = async (tenant, status) => {
    if (status === tenant.status) return;
    setActionError('');
    setSuccessMessage('');
    setBusyId(tenant._id);
    try {
      await platformService.updateTenantStatus(tenant._id, status);
      load();
    } catch (err) {
      setActionError(err.response?.data?.messages?.error || 'Failed to update status.');
    } finally {
      setBusyId(null);
    }
  };

  const handlePlanChange = async (tenant, planCode) => {
    if (planCode === tenant.plan_code) return;
    setActionError('');
    setSuccessMessage('');
    setBusyId(tenant._id);
    try {
      await platformService.updateTenantPlan(tenant._id, planCode);
      load();
    } catch (err) {
      setActionError(err.response?.data?.messages?.error || 'Failed to update plan.');
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Tenants</h1>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <p className="text-green-700 text-sm">{successMessage}</p>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-600 text-sm">{actionError}</p>
        </div>
      )}

      <div className="card overflow-x-auto">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading...</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="px-6 py-3">Tenant</th>
                <th className="px-6 py-3 text-center">Users</th>
                <th className="px-6 py-3">Plan &amp; Contract</th>
                <th className="px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t._id} className="border-b border-gray-100 last:border-0 align-top">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 break-all">
                      {t.subdomain}
                      {t.is_default && <span className="ml-2 badge bg-gray-100 text-gray-600">default</span>}
                    </div>
                    {t.name && t.name !== t.subdomain && <div className="text-gray-600">{t.name}</div>}
                    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                      <ContactLine label="Email" value={t.admin_email} href={t.admin_email ? `mailto:${t.admin_email}` : null} />
                      <ContactLine label="Phone" value={t.admin_phone} href={t.admin_phone ? toWhatsAppLink(t.admin_phone) : null} external />
                      <ContactLine label="Company" value={t.company_phone} href={t.company_phone ? toWhatsAppLink(t.company_phone) : null} external />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-center">{t.user_count}</td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <select
                        className="input-field !w-auto text-sm py-1"
                        value={t.plan_code || ''}
                        disabled={busyId === t._id}
                        onChange={(e) => handlePlanChange(t, e.target.value)}
                      >
                        {plans.map((p) => (
                          <option key={p.code} value={p.code}>{p.name}</option>
                        ))}
                      </select>
                      <BillingCell tenant={t} />
                      <button
                        type="button"
                        className="btn-secondary !px-3 !py-1 text-xs whitespace-nowrap"
                        disabled={busyId === t._id || plans.length === 0}
                        onClick={() => { setSuccessMessage(''); setContractTenant(t); }}
                      >
                        {t.billing_state === 'contract' ? 'Edit Contract' : 'Assign Contract'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      className={`badge border-0 text-sm py-1 ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-700'}`}
                      value={t.status}
                      disabled={busyId === t._id || t.is_default}
                      title={t.is_default ? 'Default tenant status is managed automatically' : ''}
                      onChange={(e) => handleStatusChange(t, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {contractTenant && (
        <ManualContractModal
          tenant={contractTenant}
          plans={plans}
          onCancel={() => setContractTenant(null)}
          onSaved={handleContractSaved}
        />
      )}
    </div>
  );
};

export default TenantsPage;
